// components/CRM/CustomColumnManager.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createAxiosInstance } from '../utils/Axios';

const CustomColumnManager = ({ 
  isOpen, 
  onClose, 
  tenantId, 
  visibleColumns, 
  columnNames, 
  setVisibleColumns, 
  setColumnNames,
  onColumnsUpdate 
}) => {
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newField, setNewField] = useState({
    fieldName: '',
    fieldKey: '',
    fieldType: 'text',
    options: [],
    required: false
  });
  const [editingField, setEditingField] = useState(null);

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchCustomFields();
    }
  }, [isOpen, tenantId]);

  const fetchCustomFields = async () => {
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      const response = await axiosInstance.get(`${apiUrl}/lead-fields/tenant/${tenantId}`);
      
      if (response.data && response.data.success) {
        setCustomFields(response.data.fields || []);
        
        // Auto-add any new custom fields to visible columns if not previously defined
        const newFields = response.data.fields || [];
        newFields.forEach(field => {
          const colKey = `custom_${field.fieldKey}`;
          setColumnNames(prev => ({ ...prev, [colKey]: field.fieldName }));
          setVisibleColumns(prev => {
            if (prev[colKey] === undefined) {
              return { ...prev, [colKey]: true };
            }
            return prev;
          });
        });
      }
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      toast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = async () => {
    if (!newField.fieldName.trim() || !newField.fieldKey.trim()) {
      toast.error('Please enter both field name and key');
      return;
    }

    // Generate safe field key
    const fieldKey = newField.fieldKey.trim().toLowerCase().replace(/\s+/g, '_');
    
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      const payload = {
        tenantId,
        fieldName: newField.fieldName.trim(),
        fieldKey: fieldKey,
        fieldType: newField.fieldType,
        options: newField.fieldType === 'dropdown' ? newField.options : [],
        required: newField.required
      };

      const response = await axiosInstance.post(`${apiUrl}/leads/field`, payload);
      
      if (response.data && response.data.success) {
        toast.success('Field added successfully!');
        
        // Add to visible columns
        const colKey = `custom_${fieldKey}`;
        setVisibleColumns(prev => ({ ...prev, [colKey]: true }));
        setColumnNames(prev => ({ ...prev, [colKey]: newField.fieldName.trim() }));
        
        // Refresh the list
        fetchCustomFields();
        setIsAdding(false);
        setNewField({
          fieldName: '',
          fieldKey: '',
          fieldType: 'text',
          options: [],
          required: false
        });
        
        if (onColumnsUpdate) onColumnsUpdate();
      }
    } catch (error) {
      console.error('Failed to add field:', error);
      toast.error(error.response?.data?.message || 'Failed to add field');
    }
  };

  const handleDeleteField = async (fieldId, fieldKey) => {
    if (!window.confirm('Are you sure you want to delete this field? This will also remove it from all leads.')) {
      return;
    }

    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      const response = await axiosInstance.delete(`${apiUrl}/lead-fields/${fieldId}`);
      
      if (response.data && response.data.success) {
        toast.success('Field deleted successfully!');
        
        // Remove from visible columns
        const colKey = `custom_${fieldKey}`;
        setVisibleColumns(prev => {
          const newState = { ...prev };
          delete newState[colKey];
          return newState;
        });
        setColumnNames(prev => {
          const newState = { ...prev };
          delete newState[colKey];
          return newState;
        });
        
        fetchCustomFields();
        if (onColumnsUpdate) onColumnsUpdate();
      }
    } catch (error) {
      console.error('Failed to delete field:', error);
      toast.error(error.response?.data?.message || 'Failed to delete field');
    }
  };

  const handleUpdateField = async (fieldId, updates) => {
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      const response = await axiosInstance.put(`${apiUrl}/lead-fields/${fieldId}`, updates);
      
      if (response.data && response.data.success) {
        toast.success('Field updated successfully!');
        fetchCustomFields();
        setEditingField(null);
        if (onColumnsUpdate) onColumnsUpdate();
      }
    } catch (error) {
      console.error('Failed to update field:', error);
      toast.error(error.response?.data?.message || 'Failed to update field');
    }
  };

  const getFieldTypeColor = (type) => {
    const colors = {
      text: '#3B82F6',
      number: '#10B981',
      date: '#F59E0B',
      dropdown: '#8B5CF6',
      checkbox: '#EF4444'
    };
    return colors[type] || '#6B7280';
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '650px',
          maxWidth: '95%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#111827' }}>
              Manage Custom Columns
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6B7280' }}>
              Create and manage custom fields for your leads
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6B7280',
              fontSize: '20px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {/* Add Field Button */}
          {!isAdding && (
            <button
              onClick={() => setIsAdding(true)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px dashed #D1D5DB',
                borderRadius: '8px',
                backgroundColor: '#F9FAFB',
                color: '#6B7280',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.borderColor = '#8B5CF6';
                e.currentTarget.color = '#8B5CF6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.borderColor = '#D1D5DB';
                e.currentTarget.color = '#6B7280';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add New Custom Column
            </button>
          )}

          {/* Add Field Form */}
          {isAdding && (
            <div
              style={{
                padding: '16px',
                border: '2px solid #8B5CF6',
                borderRadius: '8px',
                backgroundColor: '#F5F3FF',
                marginBottom: '20px'
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Field Name *
                  </label>
                  <input
                    type="text"
                    value={newField.fieldName}
                    onChange={(e) => setNewField({ ...newField, fieldName: e.target.value })}
                    placeholder="e.g. Budget Range"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Field Key *
                  </label>
                  <input
                    type="text"
                    value={newField.fieldKey}
                    onChange={(e) => setNewField({ ...newField, fieldKey: e.target.value })}
                    placeholder="e.g. budget_range"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Field Type
                  </label>
                  <select
                    value={newField.fieldType}
                    onChange={(e) => setNewField({ ...newField, fieldType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none',
                      backgroundColor: '#fff'
                    }}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="checkbox">Checkbox</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', paddingTop: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={newField.required}
                      onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                    />
                    Required Field
                  </label>
                </div>
              </div>

              {newField.fieldType === 'dropdown' && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>
                    Options (comma separated)
                  </label>
                  <input
                    type="text"
                    value={newField.options.join(', ')}
                    onChange={(e) => setNewField({ 
                      ...newField, 
                      options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                    })}
                    placeholder="e.g. Low, Medium, High"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      outline: 'none'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setNewField({
                      fieldName: '',
                      fieldKey: '',
                      fieldType: 'text',
                      options: [],
                      required: false
                    });
                  }}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#6B7280',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddField}
                  style={{
                    padding: '6px 16px',
                    backgroundColor: '#8B5CF6',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Add Field
                </button>
              </div>
            </div>
          )}

          {/* Existing Fields List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading custom fields...
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {customFields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>
                  <p style={{ fontSize: '14px', margin: 0 }}>
                    No custom columns yet. Click "Add New Custom Column" to get started.
                  </p>
                </div>
              ) : (
                customFields.map((field) => (
                  <div
                    key={field._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      border: '1px solid #F3F4F6'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          backgroundColor: getFieldTypeColor(field.fieldType),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}
                      >
                        {field.fieldType.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                          {field.fieldName}
                          {field.required && (
                            <span style={{ marginLeft: '6px', color: '#EF4444', fontSize: '12px' }}>*</span>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          Key: {field.fieldKey} • Type: {field.fieldType}
                          {field.fieldType === 'dropdown' && field.options.length > 0 && (
                            <> • Options: {field.options.join(', ')}</>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => {
                          const colKey = `custom_${field.fieldKey}`;
                          setVisibleColumns(prev => ({
                            ...prev,
                            [colKey]: !prev[colKey]
                          }));
                          if (onColumnsUpdate) onColumnsUpdate();
                        }}
                        style={{
                          padding: '4px 10px',
                          backgroundColor: visibleColumns[`custom_${field.fieldKey}`] ? '#D1FAE5' : '#F3F4F6',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: visibleColumns[`custom_${field.fieldKey}`] ? '#065F46' : '#6B7280',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        {visibleColumns[`custom_${field.fieldKey}`] ? 'Visible' : 'Hidden'}
                      </button>
                      <button
                        onClick={() => handleDeleteField(field._id, field.fieldKey)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#FEF2F2',
                          border: 'none',
                          borderRadius: '4px',
                          color: '#EF4444',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#F3F4F6',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomColumnManager;